import { defineStore } from "pinia";
import { AssetManagementPlan } from "@/classes";
import { computed, ref } from "vue";
import NuxtAxiosInstance from "@/plugins/axios";

export type PreventativeMaintenanceTactic = {
  maint_plan_id: string;
  maint_plan_description: string;
  asset_name: string;
  num_workorders: number;
  num_tactic_ops: number;
};

export type PreventativeMaintenanceData = {
  tactics: PreventativeMaintenanceTactic[];
  num_assets_applied: number;
  num_assets: number;
  num_workorders: number;
  num_tactic_ops: number;
};

export const useAMPStore = defineStore("amp", () => {
  const $axios = NuxtAxiosInstance;
  const plan = ref<AssetManagementPlan | null>(null);
  const preventativeMaintenance = ref<PreventativeMaintenanceData>({
    tactics: [],
    num_assets_applied: 0,
    num_assets: 0,
    num_workorders: 0,
    num_tactic_ops: 0,
  });

  const abortController = ref(new AbortController());
  const preventativeMaintenanceFetches = ref(0);
  const fetchingPreventativeMaintenance = computed(
    () => preventativeMaintenanceFetches.value > 0,
  );

  const getPreventativeMaintenance = async () => {
    if (plan.value) {
      try {
        abortController.value.abort();
        abortController.value = new AbortController();
        preventativeMaintenanceFetches.value++;
        preventativeMaintenance.value =
          await $axios.$post<PreventativeMaintenanceData>(
            "/asset-management-plan/preventative-maintenance-data",
            {
              floc_ids: plan.value.floc_boundaries.map(
                (b) => b.functional_location_id,
              ),
            },
            {
              signal: abortController.value.signal,
            },
          );
      } catch (e) {
        // Aborted
      } finally {
        preventativeMaintenanceFetches.value--;
      }
    }
  };

  return {
    plan,
    preventativeMaintenance,
    fetchingPreventativeMaintenance,
    getPreventativeMaintenance,
  };
});
